import type { User } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  conversationService,
  isConversationUnread,
  notificationService,
  type Conversation,
} from '@/services';
import { useBlocks } from './useBlocks';

/** Conversation write actions — no subscription, safe to use per-row. */
export function useConversationActions(user: User | null) {
  const startConversation = useCallback(
    async (targetUserId: string) => {
      if (!user) throw new Error('User not authenticated');
      try {
        return await conversationService.createConversation([user.uid, targetUserId]);
      } catch (err) {
        console.error('Conversation creation failure:', err);
        throw err;
      }
    },
    [user]
  );

  const markConversationRead = useCallback(
    async (conversationId: string) => {
      if (!user) throw new Error('User not authenticated');
      await conversationService.markConversationRead(user.uid, conversationId);
    },
    [user]
  );

  const markConversationUnread = useCallback(
    async (conversationId: string) => {
      if (!user) throw new Error('User not authenticated');
      await conversationService.markConversationUnread(user.uid, conversationId);
    },
    [user]
  );

  const archiveConversation = useCallback(
    async (conversationId: string) => {
      if (!user) throw new Error('User not authenticated');
      await conversationService.archiveConversation(user.uid, conversationId);
      await notificationService
        .dismissNotificationsForConversation(conversationId)
        .catch(err => console.error('Failed to dismiss chat notifications:', err));
    },
    [user]
  );

  return { startConversation, markConversationRead, markConversationUnread, archiveConversation };
}

export function useConversations(user: User | null, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const { blockedUids, blockedByUids } = useBlocks(user);
  const [retryKey, setRetryKey] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  // Query for conversations data (filled by subscription below)
  const { data } = useQuery<Conversation[]>({
    queryKey: ['conversations', user?.uid],
    // placeholder only; subscription fills the cache
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    const unsubscribe = conversationService.subscribeToConversations(
      user.uid,
      updatedConversations => {
        setError(null);
        queryClient.setQueryData(['conversations', user.uid], updatedConversations);
      },
      err => {
        setError(err);
      },
      enabled
    );

    return () => unsubscribe();
  }, [user, queryClient, retryKey, enabled]);

  const conversations = data ?? [];

  // Filter conversations to exclude blocked users, archived threads, and empty threads
  const filteredConversations = conversations.filter(conv => {
    const otherId = conv.userUids.find((id: string) => id !== user?.uid);
    return (
      otherId &&
      !blockedUids.includes(otherId) &&
      !blockedByUids.includes(otherId) &&
      !(user && conv.archivedBy?.includes(user.uid)) &&
      Boolean(conv.lastMessage?.trim())
    );
  });

  // Unread totals from conversation.unreadCount (cleared via markConversationRead on ChatPage open).
  const unreadCount = user
    ? filteredConversations.reduce((sum, conv) => sum + (conv.unreadCount?.[user.uid] ?? 0), 0)
    : 0;

  // True when any thread has unread messages or a manual "marked unread" flag.
  const hasUnread = user
    ? filteredConversations.some(conv => isConversationUnread(conv, user.uid))
    : false;

  const { startConversation, markConversationRead, markConversationUnread, archiveConversation } =
    useConversationActions(user);

  const refetch = useCallback(() => {
    setError(null);
    setRetryKey(prev => prev + 1);
  }, []);

  return {
    conversations,
    filteredConversations,
    blockedUids,
    blockedByUids,
    isLoading: Boolean(user && data === undefined && !error),
    isError: error != null,
    error,
    refetch,
    unreadCount,
    hasUnread,
    startConversation,
    markConversationRead,
    markConversationUnread,
    archiveConversation,
  };
}
